import { DEFAULT_BRAND, HEX_COLOUR, brandTokens, readable, type Branding } from '@cyberscore/contracts';
import { FolderOpen, Info, LayoutGrid, Lock, Loader2, RotateCcw, ShieldCheck, Sparkles } from 'lucide-react';
import { useId, useState, type CSSProperties } from 'react';
import { ApiError } from '../../lib/api';
import { RecordFrame, RecordHero, type FolderTab } from '../../ui/record/Record';
import { useToast } from '../../ui/Toasts';
import { useBranding, useSetBranding } from './api';

/** Score bands keep these colours in every tenant (badges.ts BAND_COLOUR). */
const BANDS: [string, string][] = [
  ['Poor', '#df1c41'],
  ['Fair', '#fd7521'],
  ['Good', '#007ef9'],
  ['Very good', '#28a98f'],
  ['Excellent', '#1f8f78'],
];

/**
 * The organisation's colour (task 97, decision 0073). An insurer or broker sets the colour its people see;
 * the platform also sets the defaults, for insurers and brokers without one and for business owners.
 * Everything else the app draws is derived from the one colour.
 * `embedded`: inside Tenant 360's tab (task 102), under a smaller heading.
 */
export function BrandingPage({ embedded = false }: { embedded?: boolean } = {}) {
  const q = useBranding();
  if (q.isPending) return <div className="empty">Loading…</div>;
  if (q.isError)
    return (
      <div className="empty">
        <strong>Could not load the branding</strong>
        {q.error.message}
      </div>
    );
  return <BrandingForm key={`${q.data.colour}:${q.data.owner_colour}`} b={q.data} embedded={embedded} />;
}

function BrandingForm({ b, embedded }: { b: Branding; embedded: boolean }) {
  const platform = b.kind === 'platform';
  const save = useSetBranding();
  const toast = useToast();
  const [colour, setColour] = useState(b.colour);
  const [owner, setOwner] = useState(b.owner_colour);
  const [error, setError] = useState<string | null>(null);
  const changed = colour !== b.colour || (platform && owner !== b.owner_colour);
  const valid = [colour, owner].every((c) => c === null || HEX_COLOUR.test(c));

  const submit = async () => {
    setError(null);
    try {
      await save.mutateAsync({
        ...(colour !== b.colour ? { colour } : {}),
        ...(platform && owner !== b.owner_colour ? { owner_colour: owner } : {}),
      });
      toast('Saved. Everyone sees the new colour the next time they open the app.', 'ok');
    } catch (err) {
      setError(
        err instanceof ApiError ? (err.issues[0]?.message ?? err.message) : 'Could not save. Try again.',
      );
    }
  };

  return (
    <>
      <div className={`page-head${embedded ? ' embedded' : ''}`}>
        {embedded ? <h2>Branding</h2> : <h1>Branding</h1>}
        <p>
          {platform
            ? 'The colours everyone on the platform sees unless their organisation picks its own.'
            : 'The colour your people see across CyberScore.'}{' '}
          Buttons, links, headers and tabs are all drawn from it.
        </p>
      </div>
      <div className="stack">
        <ColourCard
          title={platform ? 'Insurers, brokers and platform staff' : 'Your colour'}
          hint={platform ? 'Used by every insurer and broker that hasn’t picked its own.' : null}
          value={colour}
          fallback={platform ? DEFAULT_BRAND.staff : (b.platform.colour ?? DEFAULT_BRAND.staff)}
          fallbackLabel={platform || !b.platform.colour ? 'CyberScore’s teal' : 'the platform’s colour'}
          app="staff"
          readOnly={!b.can_edit}
          onChange={setColour}
        />
        {platform && (
          <ColourCard
            title="Business owners"
            hint="What businesses see when they answer their Cyber Check."
            value={owner}
            fallback={DEFAULT_BRAND.owner}
            fallbackLabel="CyberScore’s blue"
            app="owner"
            readOnly={!b.can_edit}
            onChange={setOwner}
          />
        )}
        <section className="card pad brand-fixed" aria-labelledby="brand-bands">
          <h2 id="brand-bands" className="section-title row gap-s">
            <Lock size={15} /> Score colours
          </h2>
          <p className="muted small">
            The same for every organisation, so a score reads the same in the app, on public badges and in
            insurer reports.
          </p>
          <div className="brand-bands">
            {BANDS.map(([label, c]) => (
              <span key={label} className="brand-band">
                <i style={{ background: c }} />
                {label}
              </span>
            ))}
          </div>
        </section>
        {error && (
          <div className="note bad" role="alert">
            <Info size={16} />
            <span>{error}</span>
          </div>
        )}
        {b.can_edit && (
          <div className="row gap">
            <button type="button" className="btn btn-primary" onClick={submit} disabled={!changed || !valid || save.isPending}>
              {save.isPending && <Loader2 size={15} className="spin" />}
              Save colours
            </button>
          </div>
        )}
      </div>
    </>
  ); // prettier-ignore
}

function ColourCard({
  title,
  hint,
  value,
  fallback,
  fallbackLabel,
  app,
  readOnly,
  onChange,
}: {
  title: string;
  hint: string | null;
  value: string | null;
  fallback: string;
  fallbackLabel: string;
  app: 'staff' | 'owner';
  readOnly: boolean;
  onChange: (c: string | null) => void;
}) {
  const id = useId();
  const shown = value ?? fallback;
  const [text, setText] = useState(shown);
  const ok = HEX_COLOUR.test(text.trim().toLowerCase());
  const safe = readable(shown);
  const set = (c: string) => {
    setText(c);
    const v = c.trim().toLowerCase();
    if (HEX_COLOUR.test(v)) onChange(v);
  };
  return (
    <section className="card pad brand-card" aria-labelledby={`${id}-h`}>
      <div className="row-between">
        <h2 id={`${id}-h`} className="section-title">{title}</h2>
        <span className={`pill ${value ? 'ok' : 'neutral'}`}>{value ? 'Your colour' : `Default: ${fallbackLabel}`}</span>
      </div>
      {hint && <p className="muted small">{hint}</p>}
      <div className="brand-edit">
        <div className="brand-pick">
          <input type="color" aria-label={`${title}: colour`} value={shown} disabled={readOnly} onChange={(e) => set(e.target.value)} />
          <input
            id={id}
            className={`input mono${ok ? '' : ' invalid'}`}
            aria-label={`${title}: hex`}
            value={text}
            maxLength={7}
            disabled={readOnly}
            onChange={(e) => set(e.target.value)}
          />
          {value && !readOnly && (
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => { setText(fallback); onChange(null); }}>
              <RotateCcw size={13} /> Use {fallbackLabel}
            </button>
          )}
        </div>
        {!ok && <span className="error">Use a colour like {DEFAULT_BRAND.staff}</span>}
        {safe !== shown && (
          <p className="small brand-adjusted">
            <Info size={14} /> Too light for white text: buttons and links use <span className="mono">{safe}</span>, a darker shade of it.
          </p>
        )}
      </div>
      <Preview colour={shown} app={app} />
    </section>
  ); // prettier-ignore
}

const PREVIEW_TABS: FolderTab[] = [
  { id: '360', label: 'Risk 360', icon: LayoutGrid },
  { id: 'score', label: 'Score', icon: ShieldCheck },
  { id: 'evidence', label: 'Evidence', icon: FolderOpen },
];

/** The colour on the things people see most, drawn with the same tokens and components the app uses. */
function Preview({ colour, app }: { colour: string; app: 'staff' | 'owner' }) {
  const style = brandTokens(colour, app) as CSSProperties;
  const [tab, setTab] = useState('360');
  return (
    <div className={`brand-preview${app === 'owner' ? ' owner' : ''}`} style={style} aria-label="Preview">
      <RecordFrame>
        <RecordHero
          crumbs={[{ label: 'Clients' }, { label: 'Client 360' }]}
          title="Northstar Manufacturing"
          pill={<span className="pill neutral">In underwriting</span>}
          subline={['CS-2026-0482', 'Manufacturing']}
          actions={<span className="rec-ask"><Sparkles size={16} /> Ask CyberScore AI</span>}
          tabs={PREVIEW_TABS}
          active={tab}
          onSelect={setTab}
          tabsLabel="Preview tabs"
        />
        <div className="brand-preview-row">
          <button type="button" className="btn btn-primary btn-sm" tabIndex={-1}>Open decision</button>
          <a href="#preview" onClick={(e) => e.preventDefault()} tabIndex={-1}>Review control</a>
          <span className="brand-swatches" aria-hidden="true">
            {['--brand-base-75', '--brand-base-200', '--primary'].map((t) => <i key={t} style={{ background: `var(${t})` }} />)}
          </span>
        </div>
      </RecordFrame>
    </div>
  ); // prettier-ignore
}
